import type { Area, AreaVersion, GeoPolygon, LatLng, OutsideArea, Pin } from "./api";

export const AREA_FILL_OPACITY = 0.3;

/** The outline colour: a darker shade of the fill, so two areas of one week stay apart. */
export function darker(hex: string, keep = 0.6): string {
  const value = Number.parseInt(hex.slice(1), 16);
  const channels = [16, 8, 0].map((shift) => Math.round(((value >> shift) & 0xff) * keep));
  return `#${channels.map((channel) => channel.toString(16).padStart(2, "0")).join("")}`;
}
export const AREAS_PARAM = "areas";

function pointsOf(positions: number[][]): LatLng[] {
  const points = positions.map(([lng, lat]) => ({ lat, lng }));
  const last = points.at(-1);
  if (points.length > 1 && last && last.lat === points[0].lat && last.lng === points[0].lng) points.pop();
  return points.length >= 3 ? points : [];
}

/** The outline of a polygon as map points, without the closing point; empty when it has no area. */
export const ringOf = (polygon: GeoPolygon | null): LatLng[] => pointsOf(polygon?.coordinates[0] ?? []);

/** The outline and the holes, for the map to draw; a hole is an area inside this one. */
export function pathsOf(polygon: GeoPolygon | null): LatLng[][] {
  const [outline = [], ...holes] = polygon?.coordinates ?? [];
  const ring = pointsOf(outline);
  return ring.length ? [ring, ...holes.map(pointsOf).filter((hole) => hole.length)] : [];
}

/** The centroid of the outline, where the map shows the area code. */
export function labelPoint(ring: LatLng[]): LatLng {
  let twice = 0;
  let lat = 0;
  let lng = 0;
  ring.forEach((point, index) => {
    const next = ring[(index + 1) % ring.length];
    const cross = point.lng * next.lat - next.lng * point.lat;
    twice += cross;
    lng += (point.lng + next.lng) * cross;
    lat += (point.lat + next.lat) * cross;
  });
  if (Math.abs(twice) < 1e-12) {
    return { lat: ring.reduce((sum, p) => sum + p.lat, 0) / ring.length, lng: ring.reduce((sum, p) => sum + p.lng, 0) / ring.length };
  }
  return { lat: lat / (3 * twice), lng: lng / (3 * twice) };
}

/** The map shows the areas unless the URL turns them off. */
export const readAreas = (search: string) => new URLSearchParams(search).get(AREAS_PARAM) !== "0";

/** The search part of the URL with the Areas choice; on is the default and has no parameter. */
export function writeAreas(search: string, on: boolean): string {
  const params = new URLSearchParams(search);
  if (on) params.delete(AREAS_PARAM);
  else params.set(AREAS_PARAM, "0");
  const text = params.toString();
  return text ? `?${text}` : "";
}

export const MAX_CORNERS = 500;
const MIN_AREA_SQ_DEG = 1e-12;

const round6 = (value: number) => Math.round(value * 1e6) / 1e6;
const same = (a: LatLng, b: LatLng) => a.lat === b.lat && a.lng === b.lng;

/** The corners to 6 decimals, as the API keeps them, without a corner that repeats the one before it. */
export function normalRing(points: LatLng[]): LatLng[] {
  const ring: LatLng[] = [];
  for (const point of points) {
    const next = { lat: round6(point.lat), lng: round6(point.lng) };
    if (!ring.length || !same(ring[ring.length - 1], next)) ring.push(next);
  }
  while (ring.length > 1 && same(ring[0], ring[ring.length - 1])) ring.pop();
  return ring;
}

/** The closed GeoJSON outline of the corners, [lng, lat]. */
export function polygonOf(ring: LatLng[]): GeoPolygon {
  const positions = ring.map(({ lat, lng }) => [lng, lat]);
  return { type: "Polygon", coordinates: [[...positions, positions[0]]] };
}

export const sameRing = (a: LatLng[], b: LatLng[]) => a.length === b.length && a.every((point, index) => same(point, b[index]));

function ringArea(ring: LatLng[]): number {
  let twice = 0;
  ring.forEach((point, index) => {
    const next = ring[(index + 1) % ring.length];
    twice += point.lng * next.lat - next.lng * point.lat;
  });
  return twice / 2;
}

type XY = [number, number];
const cross = (o: XY, a: XY, b: XY) => (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
const onSegment = (p: XY, a: XY, b: XY) =>
  Math.min(a[0], b[0]) <= p[0] && p[0] <= Math.max(a[0], b[0]) && Math.min(a[1], b[1]) <= p[1] && p[1] <= Math.max(a[1], b[1]);

function crossOrTouch(a: XY, b: XY, c: XY, d: XY): boolean {
  const [d1, d2, d3, d4] = [cross(c, d, a), cross(c, d, b), cross(a, b, c), cross(a, b, d)];
  if (d1 !== 0 && d2 !== 0 && d3 !== 0 && d4 !== 0 && d1 > 0 !== d2 > 0 && d3 > 0 !== d4 > 0) return true;
  return (d1 === 0 && onSegment(a, c, d)) || (d2 === 0 && onSegment(b, c, d)) || (d3 === 0 && onSegment(c, a, b)) || (d4 === 0 && onSegment(d, a, b));
}

/** True when two edges of the closed outline cross, touch or fold back, as the API checks it. */
export function selfCrossing(ring: LatLng[]): boolean {
  const corners: XY[] = ring.map(({ lat, lng }) => [lng, lat]);
  const count = corners.length;
  for (let i = 0; i < count; i++) {
    const a = corners[i];
    const b = corners[(i + 1) % count];
    const following = corners[(i + 2) % count];
    if (cross(a, b, following) === 0 && (onSegment(following, a, b) || onSegment(a, b, following))) return true;
    for (let j = i + 2; j < count; j++) {
      if (i === 0 && j === count - 1) continue;
      if (crossOrTouch(a, b, corners[j], corners[(j + 1) % count])) return true;
    }
  }
  return false;
}

/** Rule V-17, the same checks as the API: what is wrong with the outline, or null. */
export function shapeProblem(ring: LatLng[]): string | null {
  if (ring.some(({ lat, lng }) => !Number.isFinite(lat) || !Number.isFinite(lng) || Math.abs(lat) > 90 || Math.abs(lng) > 180)) return "A corner is not on the map.";
  if (ring.length < 3) return "An area needs at least 3 corners.";
  if (ring.length > MAX_CORNERS) return `An area has at most ${MAX_CORNERS} corners.`;
  if (new Set(ring.map(({ lat, lng }) => `${lat},${lng}`)).size !== ring.length) return "Two corners are at the same place.";
  if (selfCrossing(ring)) return "The edges of the outline cross. Move a corner.";
  if (Math.abs(ringArea(ring)) < MIN_AREA_SQ_DEG) return "The outline has no area.";
  return null;
}

const NO_VIEW = { southWest: { lat: 39.3, lng: -99.1 }, northEast: { lat: 40.3, lng: -98.1 } };

/** A triangle in the middle of the view, for an area that has no shape yet. */
export function starterRing(southWest: LatLng | null, northEast: LatLng | null): LatLng[] {
  const low = southWest ?? NO_VIEW.southWest;
  const high = northEast ?? NO_VIEW.northEast;
  const lat = (low.lat + high.lat) / 2;
  const lng = (low.lng + high.lng) / 2;
  const dLat = (high.lat - low.lat) / 4;
  const dLng = (high.lng - low.lng) / 4;
  return normalRing([
    { lat: lat - dLat, lng: lng - dLng },
    { lat: lat - dLat, lng: lng + dLng },
    { lat: lat + dLat, lng },
  ]);
}

function inRing(point: LatLng, ring: LatLng[]): boolean {
  let inside = false;
  ring.forEach((a, index) => {
    const b = ring[(index + ring.length - 1) % ring.length];
    if (a.lat > point.lat !== b.lat > point.lat) {
      const lng = ((b.lng - a.lng) * (point.lat - a.lat)) / (b.lat - a.lat) + a.lng;
      if (point.lng < lng) inside = !inside;
    }
  });
  return inside;
}

/** True when the outline holds the point and no hole does. */
export function holds(polygon: GeoPolygon | null, point: LatLng): boolean {
  const [outline, ...holes] = pathsOf(polygon);
  return Boolean(outline) && inRing(point, outline) && !holes.some((hole) => inRing(point, hole));
}

/** Rule V-20: the area whose shape holds the point. A drawn shape wins, then the first code. */
export function areaOfPoint<T extends Pick<Area, "id" | "code" | "polygon" | "polygon_source">>(areas: T[], point: LatLng | null): T | null {
  if (!point) return null;
  const ranked = [...areas].sort(
    (a, b) => Number(a.polygon_source !== "user") - Number(b.polygon_source !== "user") || a.code.localeCompare(b.code) || a.id.localeCompare(b.id),
  );
  return ranked.find((area) => holds(area.polygon, point)) ?? null;
}

/** The words of the outside_area flag: the area of the pin, then the area of the plan. */
export const outsideText = (flag: OutsideArea) =>
  `The pin is in ${flag.pin_area}; ${flag.sheet ? "the sheet says" : "the plan says"} ${flag.area}.`;

export const OUTSIDE_HELP =
  "The shape of an area is the truth. The account stays in the area of its plan until a person changes it. Move the pin, change the area of the plan (on the sheet while the sheet owns the account), or change the shape.";

export const VERSION_SOURCE_TEXT: Record<AreaVersion["source"], string> = {
  auto: "Proposed",
  user: "Drawn",
  revert: "Reverted",
};

const ACTOR_TEXT: Record<string, string> = {
  "area-hulls": "the night job",
  "migration-0190": "the shape before the history",
};

export const versionWhen = (version: AreaVersion, timeZone?: string) =>
  new Date(version.at).toLocaleString(undefined, { timeZone, year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit", timeZoneName: "short" });

/** One line of the shape history: when, how, who and the corners. */
export function versionText(version: AreaVersion, timeZone?: string): string {
  const when = versionWhen(version, timeZone);
  const corners = version.polygon ? `${version.vertices} corners` : "no shape";
  return `${when} · ${VERSION_SOURCE_TEXT[version.source] ?? version.source} · ${versionActor(version)} · ${corners}`;
}

/** A person shows by email; a CI job or a service account shows as the system, never by its id. */
export function versionActor(version: Pick<AreaVersion, "actor" | "source">): string {
  const known = ACTOR_TEXT[version.actor];
  if (known) return known;
  if (version.actor.includes("@") && !version.actor.endsWith(".gserviceaccount.com")) return version.actor;
  return version.source === "auto" ? "the night job" : "the system";
}

/** The area of the plan of a map pin; an older API sends no area. */
export const pinAreaText = (pin: Pick<Pin, "area">) => (pin.area === undefined ? null : pin.area ? `Area ${pin.area}` : "No area");
